import { Area, Cell, MergedRegion, Selection, Region } from './types';
import {
  pinCellToSheet,
  isCellInRegion,
  cellsEqual,
  cellsInArea,
  cellsInRegion,
  cellBoundaries,
  isCellInArea,
  filterCellsOffSheet,
} from './utils';

export type Direction = 'up' | 'down' | 'left' | 'right';

const cellInDirection = (cell: Cell, direction: Direction): Cell => {
  if (direction === 'up') {
    return { x: cell.x, y: cell.y - 1 };
  } else if (direction === 'down') {
    return { x: cell.x, y: cell.y + 1 };
  } else if (direction === 'left') {
    return { x: cell.x - 1, y: cell.y };
  } else {
    return { x: cell.x + 1, y: cell.y };
  }
};

const regionAdjacentCell = (
  region: MergedRegion,
  direction: Direction,
): Cell => {
  if (direction === 'up') {
    return { x: region.anchor.x, y: region.anchor.y - 1 };
  } else if (direction === 'down') {
    return { x: region.anchor.x, y: region.anchor.y + region.height };
  } else if (direction === 'left') {
    return { x: region.anchor.x - 1, y: region.anchor.y };
  } else {
    return { x: region.anchor.x + region.width, y: region.anchor.y };
  }
};

export const areaAdjacentCells = (
  area: Area,
  direction: Direction,
  columnCount: number,
  rowCount: number,
): Cell[] => {
  let adjacentCells: Cell[];
  const { loX, loY, hiX, hiY } = cellBoundaries(area.from, area.to);
  if (direction === 'up') {
    adjacentCells = Array.from({ length: hiX - loX + 1 }, (_, i) => ({
      x: loX + i,
      y: loY - 1,
    }));
  } else if (direction === 'down') {
    adjacentCells = Array.from({ length: hiX - loX + 1 }, (_, i) => ({
      x: loX + i,
      y: hiY + 1,
    }));
  } else if (direction === 'left') {
    adjacentCells = Array.from({ length: hiY - loY + 1 }, (_, i) => ({
      x: loX - 1,
      y: loY + i,
    }));
  } else {
    adjacentCells = Array.from({ length: hiY - loY + 1 }, (_, i) => ({
      x: hiX + 1,
      y: loY + i,
    }));
  }

  return filterCellsOffSheet(columnCount, rowCount, adjacentCells);
};

export const cellAdjacentCell = (
  cell: Cell,
  mergedRegions: readonly MergedRegion[],
  direction: Direction,
  columnCount: number,
  rowCount: number,
): Cell => {
  const baseRegion = mergedRegions.find((region) => {
    return isCellInRegion(cell, region);
  });

  const toCell = pinCellToSheet(
    columnCount,
    rowCount,
    baseRegion
      ? regionAdjacentCell(baseRegion, direction)
      : cellInDirection(cell, direction),
  );

  const toRegion = mergedRegions.find((region) => {
    return isCellInRegion(toCell, region);
  });

  return toRegion?.anchor ?? toCell;
};

export const cellAdjacentCells = (
  cell: Cell,
  mergedRegions: readonly MergedRegion[],
  direction: Direction,
  columnCount: number,
  rowCount: number,
): Cell[] => {
  const baseRegion = mergedRegions.find((region) => {
    return isCellInRegion(cell, region);
  });

  if (baseRegion) {
    return areaAdjacentCells(
      regionToArea(baseRegion),
      direction,
      columnCount,
      rowCount,
    );
  } else {
    return [
      pinCellToSheet(columnCount, rowCount, cellInDirection(cell, direction)),
    ];
  }
};

export function regionToArea(region: Region): Area {
  return {
    from: region.anchor,
    to: {
      x: region.anchor.x + region.width - 1,
      y: region.anchor.y + region.height - 1,
    },
  };
}

// Returns null if there are no cells to remove from the given direction
// Either because the base cell is adjacent to that boundary, or a removal would break up a merged region
export const shrinkingAreaForDirection = (
  baseCell: Cell,
  area: Area,
  direction: Direction,
  mergedRegions: readonly MergedRegion[],
  { offset, initRemovalCells }: { offset: number; initRemovalCells: Cell[] } = {
    offset: 1,
    initRemovalCells: [],
  },
): Area | null => {
  const { loX, loY, hiX, hiY } = cellBoundaries(area.from, area.to);
  const removalCells = initRemovalCells;
  let newArea: Area;
  if (direction === 'up') {
    if (hiY - offset < baseCell.y) {
      return null;
    }
    removalCells.push(
      ...Array.from({ length: hiX - loX + 1 }, (_, i) => ({
        x: loX + i,
        y: hiY - offset + 1,
      })),
    );
    newArea = {
      from: { x: loX, y: loY },
      to: { x: hiX, y: hiY - offset },
    };
  } else if (direction === 'down') {
    if (loY + offset > baseCell.y) {
      return null;
    }
    removalCells.push(
      ...Array.from({ length: hiX - loX + 1 }, (_, i) => ({
        x: loX + i,
        y: loY + offset - 1,
      })),
    );
    newArea = {
      from: { x: loX, y: loY + offset },
      to: { x: hiX, y: hiY },
    };
  } else if (direction === 'left') {
    if (hiX - offset < baseCell.x) {
      return null;
    }
    removalCells.push(
      ...Array.from({ length: hiY - loY + 1 }, (_, i) => ({
        x: hiX - offset + 1,
        y: loY + i,
      })),
    );
    newArea = {
      from: { x: loX, y: loY },
      to: { x: hiX - offset, y: hiY },
    };
  } else {
    if (loX + offset > baseCell.x) {
      return null;
    }
    removalCells.push(
      ...Array.from({ length: hiY - loY + 1 }, (_, i) => ({
        x: loX + offset - 1,
        y: loY + i,
      })),
    );
    newArea = {
      from: { x: loX + offset, y: loY },
      to: { x: hiX, y: hiY },
    };
  }

  for (const region of mergedRegions) {
    const regionOverlaps = removalCells.some((p) => isCellInRegion(p, region));
    if (regionOverlaps) {
      // Overlap is ok if all cells from region are removed
      const regionCells = cellsInRegion(region);
      const allCellsRemoved = regionCells.every((regionCell) =>
        removalCells.some((rp) => cellsEqual(regionCell, rp)),
      );
      if (!allCellsRemoved) {
        // Recurse with increased offset, as some regions can be removed if multiple cells are removed in the same direction
        // Optimization would be to skip offsets based on the offending regions size and direction
        return shrinkingAreaForDirection(
          baseCell,
          area,
          direction,
          mergedRegions,
          { offset: offset + 1, initRemovalCells: removalCells },
        );
      }
    }
  }

  return newArea;
};

export function areaForSelection(
  selection: Selection,
  mergedRegions: readonly MergedRegion[],
): Area {
  if (selection.area) {
    return selection.area;
  }

  const baseRegion = mergedRegions.find((region) => {
    return isCellInRegion(selection.base, region);
  });
  if (baseRegion) {
    return regionToArea(baseRegion);
  } else {
    return {
      from: selection.base,
      to: selection.base,
    };
  }
}

export const containingArea = (
  initArea: Area,
  newCells: readonly Cell[],
  mergedRegions: readonly MergedRegion[],
): Area => {
  const boundaryCells = [initArea.from, initArea.to].concat(newCells);
  const minX = Math.min(...boundaryCells.map((cell) => cell.x));
  const maxX = Math.max(...boundaryCells.map((cell) => cell.x));
  const minY = Math.min(...boundaryCells.map((cell) => cell.y));
  const maxY = Math.max(...boundaryCells.map((cell) => cell.y));
  const newArea: Area = {
    from: { x: minX, y: minY },
    to: { x: maxX, y: maxY },
  };

  // Find cells in overlapping region that are not in the new area
  const missingSameRegionCells: Cell[] = [];
  const areaCells = cellsInArea(newArea);
  for (const region of mergedRegions) {
    const regionOverlaps = areaCells.some((p) => isCellInRegion(p, region));
    if (regionOverlaps) {
      for (const cell of cellsInRegion(region)) {
        if (!isCellInArea(cell, newArea)) {
          missingSameRegionCells.push(cell);
        }
      }
    }
  }

  if (missingSameRegionCells.length === 0) {
    return newArea;
  } else {
    return containingArea(newArea, missingSameRegionCells, mergedRegions);
  }
};
